import { useContext } from 'react';
import { useParams } from 'react-router';
import { groupBy } from 'lodash';
import {
  CLU_BOUNDARIES_LAYER_ID,
  customLayerSpec,
  mapModes,
  SOIL_BOUNDARIES_LAYER_ID,
} from '../../../constants/map';
import { MapContext } from '../../../contexts/MapContext';
import DropdownButton from '../../basics/DropdownButton';
import LayersIcon from '../../icons/LayersIcon';
import { layerHasData } from '../../../utils/layers';
import { captureEvent } from '../../../utils/analytics';

// Sentinel value for new custom layer
const CUSTOM_LAYER_ID = Symbol('CUSTOM');

export const useSwitchToLayer = () => {
  const {
    mapState: { setMapMode },
  } = useContext(MapContext);

  return (layerId) => {
    setMapMode(mapModes.LAYER_ENGAGED, layerId);
  };
};

const newCustomLayer = {
  layerId: CUSTOM_LAYER_ID,
  displayName: 'Custom',
  ...customLayerSpec,
};

const NewLayerButton = (props) => {
  const { farmId } = useParams();
  const {
    layers: { layers, createCustomLayer, setLayerShown },
    mapState: { setMapMode },
  } = useContext(MapContext);
  const switchToLayer = useSwitchToLayer();

  const createLayer = async (layerId) => {
    if (layerId === CUSTOM_LAYER_ID) {
      const [id, promise] = createCustomLayer({ farmId });
      captureEvent('custom-layer-create');
      promise.catch(() => setMapMode(mapModes.VIEW));
      switchToLayer(id);
    } else {
      // non-custom layer
      captureEvent('new-layer', { layerId });
      switchToLayer(layerId);
      setLayerShown({ layerId }); // Ensure layer shown
    }
  };

  const potentialNewLayers = Object.values(layers).filter(
    (layer) =>
      !layerHasData(layer) &&
      !layer?.hasRemote &&
      // CLU boundaries deprecated, soil boundaries always in left menu
      ![CLU_BOUNDARIES_LAYER_ID, SOIL_BOUNDARIES_LAYER_ID].includes(
        layer.layerId,
      ),
  );

  // Add Custom Layer option
  potentialNewLayers.unshift(newCustomLayer);

  const options = Object.entries(
    groupBy(potentialNewLayers, 'displayGroup'),
  ).flatMap(([displayGroup, groupedLayers]) => [
    ...(displayGroup === 'undefined'
      ? []
      : [{ header: true, key: `${displayGroup}-group`, name: displayGroup }]),
    ...groupedLayers.map((layer) => ({
      ...layer,
      key: layer.displayName,
      name: layer.displayName,
      icon: layer.iconComponent,
    })),
  ]);

  const selectedOption = (
    <span className="d-flex align-items-center">
      <LayersIcon className="d-flex mr-2 align-items-center" />
      New Layer
    </span>
  );

  return (
    <DropdownButton
      options={options}
      setOption={({ layerId }) => createLayer(layerId)}
      selectedOption={selectedOption}
      {...props}
    />
  );
};

export default NewLayerButton;
